import { useEffect, useMemo, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import { FormProvider, useForm } from 'react-hook-form';

import { useCreatePayment } from 'api/hooks/private/finance/CreatePayment';
import { Charge } from 'api/hooks/private/finance/GetCharge';
import { useGetCharges } from 'api/hooks/private/finance/GetCharges';
import { Payment, useGetPayment } from 'api/hooks/private/finance/GetPayment';
import { useGetPaymentMethods } from 'api/hooks/private/finance/GetPaymentMethods';
import { useUpdatePayment } from 'api/hooks/private/finance/UpdatePayment';
import { useGetStudent } from 'api/hooks/private/users/students/GetStudent';

import DateModified from 'components/Dialogs/components/DateModified';
import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import AutocompleteInput from 'components/Forms/AutocompleteInput';
import CurrencyInput from 'components/Forms/CurrencyInput';
import SelectInput from 'components/Forms/SelectInput';
import TextInput from 'components/Forms/TextInput';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { getZodObjectDefaults } from 'utils/getZodObjectDefaults';

import PaymentAllocationTable from './components/PaymentAllocationTable';
import PaymentSummary from './components/PaymentSummary';
import { BasePaymentFormSchema, PaymentFormSchema, PaymentFormValues } from './PaymentSchema';

// interface DialogProps {
//     onClose?: () => void;
// }

// Creating a new payment
export type NewPaymentDialogParams = {
  paymentId?: null | undefined;
  studentId: string; // required if no paymentId
};

// Editing an existing payment
export type UpdatePaymentDialogParams = {
  paymentId: string;
  studentId?: never; // disallow studentId when editing existing payment
};

// Union for new or existing charge
export type OpenPaymentDialogParams = NewPaymentDialogParams | UpdatePaymentDialogParams;

export default function usePaymentDialog({
  payload: dialogState,
  open,
  onClose,
}: DialogProps<OpenPaymentDialogParams>) {
  // dialog state
  // const [dialogState, setDialogState] = useState<OpenPaymentDialogParams | null>(null);

  // flag to prevent overwriting user changes
  const hasFormInitialized = useRef(false);

  const dialogs = useDialogs();

  const form = useForm<PaymentFormValues>({
    mode: 'all',
    resolver: zodResolver(PaymentFormSchema),
  });

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = form;

  const queryClient = useQueryClient();

  // get existing payment information
  const existingPayment = useGetPayment({
    variables: {
      id: dialogState?.paymentId ?? '',
    },

    // don't use cached data
    gcTime: 0,

    // fetch only if editing an existing payment
    enabled: !!dialogState?.paymentId,
  });

  // get existing charges
  const existingCharges = useGetCharges({
    variables: {
      studentId: dialogState?.studentId ?? existingPayment.data?.student_user_id ?? '',
    },

    // don't use cached data
    gcTime: 0,

    // only fetch when studentId is available
    enabled: !!dialogState?.studentId || !!existingPayment.data?.student_user_id,
  });

  // get payment methods
  const paymentMethods = useGetPaymentMethods({
    variables: {
      // only get payment methods allowed for payments
      type: 'payments',
      enabled: true,
    },
  });

  // get student for autocomplete
  const student = useGetStudent({
    variables: {
      id: dialogState?.studentId ?? existingPayment.data?.student_user_id ?? '',
    },

    // don't use cached data
    // cacheTime: 0,

    // only fetch when studentId is available
    enabled: !!dialogState?.studentId || !!existingPayment.data?.student_user_id,
  });

  // create list of autocomplete options for received from field
  const receivedFromOptions = useMemo<string[]>(() => {
    // if the student info hasn't been fetched return an empty list
    if (!student.data) return [];

    const studentInfo = student.data;
    const options = [];

    // add the student's name to the list
    options.push([studentInfo.nickname ?? studentInfo.first_name, studentInfo.last_name].join(' '));

    // add the student's parents' names to the list
    if (studentInfo.student.parents?.length > 0) {
      options.push(...studentInfo.student.parents.map((parent) => [parent.first_name, parent.last_name].join(' ')));
    }

    // return the list of options
    return options;
  }, [student]);

  const createPaymentMutation = useCreatePayment();
  const updatePaymentMutation = useUpdatePayment();

  useEffect(() => {
    if (!dialogState.paymentId) {
      form.reset({
        ...getZodObjectDefaults(BasePaymentFormSchema),
        id: null,
        student_user_id: dialogState.studentId,
      });
    }
  }, [dialogState, form]);

  // const openPaymentDialog = (params: OpenPaymentDialogParams) => {
  //     setDialogState(params);
  //     hasFormInitialized.current = false;

  //     // Creating a new payment, reset the form with base defaults:
  //     if (!params.paymentId) {
  //         form.reset({
  //             ...getZodObjectDefaults(BasePaymentFormSchema),
  //             id: null,
  //             student_user_id: params.studentId,
  //         });
  //     }
  // };

  const handleClose = () => {
    // close the dialog
    // setDialogState(null);
    form.reset({ id: null, student_user_id: undefined });
    hasFormInitialized.current = false;
    onClose?.();
  };

  const createPayment = async (data: PaymentFormValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm('Are you sure you want to record the payment?', {
        cancelText: 'Cancel',
        severity: 'error',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      // create the payment
      const result = await createPaymentMutation.mutateAsync(data);

      if (result) {
        // close the dialog
        handleClose();

        // invalidate the existing payment query
        queryClient.invalidateQueries({
          queryKey: ['payment', { id: dialogState?.paymentId }],
        });

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Payment Created',
        });
      }
    } catch (error) {
      // show error message
      dialogs.open(FeedbackDialog, {
        title: 'Error creating payment',
        message: getApiErrorMessage(error),
      });
    }
  };

  const updatePayment = async (data: PaymentFormValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm('Are you sure you want to update the payment?', {
        cancelText: 'Cancel',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      // update the payment
      const result = await updatePaymentMutation.mutateAsync(data);

      if (result) {
        // close the dialog
        handleClose();

        // invalidate the existing payment query
        queryClient.invalidateQueries({
          queryKey: ['payment', { id: dialogState?.paymentId }],
        });

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Payment Updated',
        });
      }
    } catch (error) {
      // show error message
      dialogs.open(FeedbackDialog, {
        title: 'Error updating payment',
        message: getApiErrorMessage(error),
      });
    }
  };

  const onSubmit = (data: PaymentFormValues) => {
    console.log('Payment Form Data:', data);
    // create or update the payment
    if (dialogState?.paymentId) {
      updatePayment(data);
    } else {
      createPayment(data);
    }
  };

  //   // clear allocations handler
  //   const handleClearAllocations = () => {
  //     // get the latest allocations from form state
  //     const currentAllocations = getValues("allocations");

  //     // set all amounts to zero
  //     const clearedAllocations = currentAllocations.map((alloc) => ({
  //       ...alloc,
  //       amount: 0,
  //     }));

  //     // set it back in the form state, causing re-render
  //     setValue("allocations", clearedAllocations, {
  //       shouldValidate: true,
  //       shouldDirty: true,
  //       shouldTouch: true,
  //     });

  //     // trigger custom validation logic
  //     trigger("allocations");
  //   };

  // reset allocations handler
  const handleResetAllocations = () => {
    // get the latest allocations from form state
    const currentAllocations = getValues('allocations');

    // reset "amount" back to "previous_amount" or 0 if creating new payment
    const resetAllocations = currentAllocations.map((alloc) => ({
      ...alloc,
      amount: alloc.previous_amount ?? 0,
    }));

    // set it back in the form state, causing re-render
    setValue('allocations', resetAllocations, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    // trigger custom validation logic
    trigger('allocations');
  };

  const handleAutoAllocation = () => {
    // ensure that the charges are loaded
    if (!existingCharges.isSuccess) return;

    // get the total payment amount that can be allocated
    const paymentAmount = getValues('amount') - getValues('refunded');
    if (!paymentAmount || paymentAmount <= 0) return;

    // get current allocations from the form (unsorted, in the original order)
    const allocations = getValues('allocations');

    // sort by: rows that still need deposit first (by deposit_due),
    // then by overall due date (discounted_total_due)
    const sortedAllocations = [...allocations].sort((a, b) => {
      const aDepositNeeded = Math.max(a.deposit, 0);
      const bDepositNeeded = Math.max(b.deposit, 0);

      if (aDepositNeeded > 0 && bDepositNeeded === 0) return -1;
      if (bDepositNeeded > 0 && aDepositNeeded === 0) return 1;

      if (aDepositNeeded > 0 && bDepositNeeded > 0) {
        const aDue = a.deposit_due ? new Date(a.deposit_due).getTime() : Number.POSITIVE_INFINITY;
        const bDue = b.deposit_due ? new Date(b.deposit_due).getTime() : Number.POSITIVE_INFINITY;
        return aDue - bDue;
      } else {
        const aDue = a.discounted_total_due ? new Date(a.discounted_total_due).getTime() : Number.POSITIVE_INFINITY;
        const bDue = b.discounted_total_due ? new Date(b.discounted_total_due).getTime() : Number.POSITIVE_INFINITY;
        return aDue - bDue;
      }
    });

    // create a lookup map for new allocation amounts
    const allocationUpdates = new Map<string, number>();
    let remainingPayment = paymentAmount;

    // allocate funds based on the sorted order
    for (const alloc of sortedAllocations) {
      if (remainingPayment <= 0) break;
      const neededAmount = alloc.remaining + alloc.previous_amount;
      const allocation = Math.min(neededAmount, remainingPayment);
      remainingPayment -= allocation;
      allocationUpdates.set(alloc.id, allocation);
    }

    // update the allocations array, preserving its original order
    const updatedAllocations = allocations.map((alloc) => ({
      ...alloc,
      amount: allocationUpdates.get(alloc.id) ?? 0,
    }));

    // update the form with the new allocations and trigger validation
    setValue('allocations', updatedAllocations, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    trigger('allocations');
  };

  // get payment allocations from unpaid charges and previously allocated charges
  const getPaymentAllocations = (charges: Charge[], payment?: Payment): PaymentFormValues['allocations'] => {
    // create map of previously allocated amounts
    const allocationMap = new Map<string, number>();
    for (const alloc of payment?.student_charge_payments ?? []) {
      allocationMap.set(alloc.student_charge_id, alloc.amount);
    }

    // filter out voided charges
    const nonVoidCharges = charges.filter((charge) => !charge.voided);

    // create form allocations from relevant charges
    // - if the charge has some remaining balance (not fully paid)
    // - or if there's an existing allocation from this payment.
    const paymentAllocations: PaymentFormValues['allocations'] = nonVoidCharges
      .map((charge) => {
        const discounted_total = Number(charge.discounted_total);
        const paid = Number(charge.amount_paid);

        // Return the item you want to include, if condition is met
        return {
          id: charge.id,
          amount: allocationMap.get(charge.id) ?? 0,
          previous_amount: allocationMap.get(charge.id) ?? 0,
          memo: charge.memo ?? '',
          discounted_total: discounted_total,
          deposit: charge.deposit,
          remaining: discounted_total - paid,
          discounted_total_due: charge?.amount_due ?? null,
          deposit_due: charge?.deposit_due ?? null,
        };
      })
      .filter((alloc) => alloc.remaining > 0 || alloc.previous_amount > 0);

    // return the payment allocations
    return paymentAllocations;
  };

  useEffect(() => {
    // if the form is already initialized, skip
    if (hasFormInitialized.current) return;

    // wait for charges to load
    if (!existingCharges.isSuccess || !existingCharges.data) return;

    // if a new payment is being created
    if (!dialogState?.paymentId) {
      const allocations = getPaymentAllocations(existingCharges.data);

      // only reset the allocations so we don't overwrite user-changed fields
      form.reset((prev) => ({
        ...prev,
        allocations,
      }));
    } else {
      // wait for existingPayment
      if (!existingPayment.isSuccess || !existingPayment.data) return;

      const allocations = getPaymentAllocations(existingCharges.data, existingPayment.data);

      form.reset({
        id: existingPayment.data.id,
        student_user_id: existingPayment.data.student_user_id,
        date: existingPayment.data.date,
        amount: existingPayment.data.amount,
        refunded: Number(existingPayment.data.amount_refunded ?? 0),
        memo: existingPayment.data.memo ?? '',
        description: existingPayment.data.description,
        private_notes: existingPayment.data.private_notes,
        payment_method_id: existingPayment.data.payment_method_id ?? undefined,
        check_no: existingPayment.data.check_no,
        received_from: existingPayment.data.received_from,
        allocations,
      });
    }

    // mark as initialized
    hasFormInitialized.current = true;
  }, [
    existingPayment.isSuccess,
    existingCharges.isSuccess,
    existingPayment.data,
    existingCharges.data,
    form,
    dialogState?.paymentId,
  ]);

  useEffect(() => {
    // show an error dialog if there was an issue fetching the existing payment data
    if (existingPayment?.isError) {
      dialogs.open(FeedbackDialog, {
        title: 'Something went wrong while fetching the existing payment info.',
        message: existingPayment?.error?.message ?? '',
      });
    }
  }, [dialogs, existingPayment?.error?.message, existingPayment?.isError]);

  useEffect(() => {
    console.log('Form Errors:', errors);
  }, [errors]);

  // useEffect(() => {
  //     // if the form hasn't been initialized, skip
  //     if (!hasFormInitialized.current) return;

  //     handleAutoAllocation();
  // }, [form.watch('amount')]);

  const PaymentDialog = (
    <QueryClientProvider client={queryClient}>
      <Dialog fullWidth open={open} onClose={handleClose} maxWidth="md">
        <DialogTitle component="div" display="flex" justifyContent="space-between">
          {dialogState?.paymentId ? 'Update Payment' : 'Record New Payment'}
        </DialogTitle>

        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <FormProvider {...form}>
            <DialogContent>
              <Grid container rowSpacing={3} columnSpacing={1}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <CurrencyInput
                    name="amount"
                    label="Total Amount"
                    textFieldProps={{
                      autoComplete: 'off',
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <SelectInput
                    name="payment_method_id"
                    label="Payment Method"
                    autoComplete="off"
                    options={paymentMethods.data?.map((paymentMethod) => ({
                      label: paymentMethod.name,
                      value: paymentMethod.id,
                    }))}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextInput
                    name="check_no"
                    label="Check Number"
                    helperText="Optional"
                    autoComplete="off"
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position="start">#</InputAdornment>,
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                </Grid>

                <Grid size={12}>
                  <TextInput name="memo" label="Memo" autoComplete="off" />
                </Grid>

                <Grid size={12}>
                  {/* <TextInput
                                        name="received_from"
                                        label="Received From"
                                        helperText="Record who paid this payment."
                                        autoComplete="off"
                                    /> */}
                  <AutocompleteInput
                    name="received_from"
                    label="Received From"
                    helperText="Record who paid this payment."
                    autoComplete="off"
                    options={receivedFromOptions}
                  />
                </Grid>

                <Grid size={12}>
                  <TextInput
                    name="description"
                    label="Description"
                    multiline
                    helperText={<span style={{ color: 'red' }}>Description visible to customer.</span>}
                    autoComplete="off"
                  />
                </Grid>

                <Grid size={12}>
                  <TextInput
                    name="private_notes"
                    label="Private Notes"
                    multiline
                    helperText="Private notes are only visible to administrators."
                    autoComplete="off"
                  />
                </Grid>

                <Grid size={12}>
                  <PaymentAllocationTable
                    resetAllocations={handleResetAllocations}
                    autoAllocatePayment={handleAutoAllocation}
                  />
                </Grid>

                <Grid size={12}>
                  <PaymentSummary />
                </Grid>

                <Grid size={12}>
                  <Box display="flex" flexDirection="column" rowGap={1}>
                    <DateModified
                      prefix="Created: "
                      date={existingPayment?.data?.created_at}
                      user={existingPayment?.data?.created_by_user}
                    />
                    <DateModified
                      prefix="Updated: "
                      date={existingPayment?.data?.updated_at}
                      user={existingPayment?.data?.updated_by_user}
                    />
                    <DateModified
                      prefix="Voided: "
                      date={existingPayment?.data?.voided_at}
                      user={existingPayment?.data?.voided_by_user}
                    />
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions
              sx={{
                flexWrap: 'wrap',
                rowGap: 2,
              }}
            >
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="submit">{dialogState?.paymentId ? 'Update' : 'Record Payment'}</Button>
            </DialogActions>
          </FormProvider>
        </form>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={
            existingPayment.isLoading ||
            existingCharges.isLoading ||
            createPaymentMutation.isPending ||
            updatePaymentMutation.isPending
          }
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Dialog>
    </QueryClientProvider>
  );

  return PaymentDialog;
}
